import { useState, useEffect, useRef } from 'react'
import { Card, Row, Col, Badge, Button, Table, Spinner, Modal, Alert, Form } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import api from '../../services/api'
import { formatCurrency } from '../../utils/formatters'
import {
  buildCfdiDownloadFilename,
  canRequestCancelacion,
  getDisplaySerieFolio,
  getFilenameFromContentDisposition,
  getInvoiceStatusBadge,
  isInvoiceCancelPending,
  isInvoiceCancelled,
  isInvoiceTimbradaVigente, esFolioProvisional, periodoDeFolioProvisional} from '../../utils/invoiceDisplay'
import { formatDateOnlyEsMX, formatDateTimeEsMX } from '../../utils/apiDateFormat'
import AccountSearchModal from '../Accounting/AccountSearchModal'
import { useAuth } from '../../hooks/useAuth'
import RevisarAsientoContableModal from './RevisarAsientoContableModal'

const InvoiceDetail = ({ invoice, onEdit, onRefresh, onInvoiceUpdated }) => {
  const { user } = useAuth()
  const CANCEL_MOTIVOS = [
    { value: '01', label: '01 - Comprobante emitido con errores con relación' },
    { value: '02', label: '02 - Comprobante emitido con errores sin relación' },
    { value: '03', label: '03 - No se llevó a cabo la operación' },
    { value: '04', label: '04 - Operación nominativa relacionada en factura global' },
  ]
  const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/

  const isComplementoPago = (invoice?.tipo_factura || '').toLowerCase() === 'complemento'

  const queryClient = useQueryClient()
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancelMotivo, setCancelMotivo] = useState('02')
  const [cancelFolioSustitucion, setCancelFolioSustitucion] = useState('')
  const [cancelNota, setCancelNota] = useState('')
  const [cancelAuthAdmin, setCancelAuthAdmin] = useState(false)
  const [cancelErrors, setCancelErrors] = useState({})
  const [cancelRepsInfo, setCancelRepsInfo] = useState([])
  const isCancelAdmin = !!(user?.is_superuser || ['superadmin', 'admin'].includes(user?.role))
  const [showEstatusModal, setShowEstatusModal] = useState(false)
  const [showPdfModal, setShowPdfModal] = useState(false)
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null)
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [descargando, setDescargando] = useState(false)
  const [estatusData, setEstatusData] = useState(null)
  const fileInputRef = useRef(null)

  // Modal cuenta contable
  const [mostrarModalCuenta, setMostrarModalCuenta] = useState(false)
  const [conceptoSeleccionadoCuenta, setConceptoSeleccionadoCuenta] = useState(null)

  // Mutación para timbrar
  const timbrarMutation = useMutation(
    async () => {
      const response = await api.post(`/financial/invoices/${invoice.id}/timbrar/`)
      return response.data
    },
    {
      onSuccess: async (_data) => {
        toast.success('Factura timbrada exitosamente')
        
        // Invalidar todas las queries relacionadas
        await queryClient.invalidateQueries(['invoice', invoice.id])
        await queryClient.invalidateQueries({ queryKey: ['invoices'], exact: false })
        
        // Refetch las queries para actualizar inmediatamente sin necesidad de refrescar
        await queryClient.refetchQueries({ queryKey: ['invoices'], exact: false })
        
        // Pequeño delay para asegurar que los datos se actualicen antes de cerrar el modal
        await new Promise(resolve => setTimeout(resolve, 300))
        
        // Si hay callback onRefresh, ejecutarlo (esto cerrará el modal y actualizará la vista)
        if (onRefresh) {
          await onRefresh()
        }
      },
      onError: (error) => {
        const errorMsg = error.response?.data?.error || 'Error al timbrar la factura'
        toast.error(errorMsg)
      }
    }
  )

  const desgloseImpuestosMutation = useMutation(
    async (checked) => {
      const response = await api.patch(`/financial/invoices/${invoice.id}/`, {
        desglose_impuestos_pdf: !!checked,
      })
      return response.data
    },
    {
      onSuccess: (data, checked) => {
        // Actualizar solo el detalle abierto; no usar onRefresh (cierra el modal).
        const updated = data && typeof data === 'object'
          ? { ...invoice, ...data, desglose_impuestos_pdf: !!data.desglose_impuestos_pdf }
          : { ...invoice, desglose_impuestos_pdf: !!checked }
        if (onInvoiceUpdated) onInvoiceUpdated(updated)
        queryClient.setQueryData(['invoice', invoice.id], (prev) =>
          prev && typeof prev === 'object' ? { ...prev, ...updated } : updated
        )
        queryClient.invalidateQueries({ queryKey: ['invoices'], exact: false })
      },
      onError: (error) => {
        toast.error(
          error.response?.data?.error
          || error.response?.data?.issue_date?.[0]
          || 'No se pudo actualizar el desglose de impuestos'
        )
      },
    }
  )
  
  // Mutación para cancelar
  const cancelarMutation = useMutation(
    async ({ motivo, folio_sustitucion, nota, autorizacion_admin }) => {
      const payload = { motivo, nota, autorizacion_admin }
      if (motivo === '01') payload.folio_sustitucion = folio_sustitucion
      const response = await api.post(`/financial/invoices/${invoice.id}/cancelar/`, payload)
      return response.data
    },
    {
      onSuccess: (data) => {
        const resultado = data?.resultado_cancelacion
        if (resultado === 'cancelada') {
          toast.success(
            isComplementoPago
              ? 'Complemento de pago (REP) cancelado ante el SAT. Se revirtieron saldos y se reabrió la CxC.'
              : 'Factura cancelada exitosamente ante el SAT'
          )
        } else {
          toast.info(
            isComplementoPago
              ? 'Solicitud de cancelación del REP enviada. Quedó en "Cancelación en proceso". Al confirmarse ante el SAT se revertirán saldos y CxC.'
              : 'Solicitud de cancelación enviada. La factura quedó en "Cancelación en proceso". Use "Consultar estatus" para sincronizar con el SAT.'
          )
        }
        queryClient.invalidateQueries(['invoice', invoice.id])
        queryClient.invalidateQueries({ queryKey: ['invoices'], exact: false })
        queryClient.invalidateQueries({ queryKey: ['payments'], exact: false })
        setShowCancelModal(false)
        setCancelMotivo('02')
        setCancelFolioSustitucion('')
        setCancelNota('')
        setCancelAuthAdmin(false)
        setCancelRepsInfo([])
        setCancelErrors({})
        if (onRefresh) onRefresh()
      },
      onError: (error) => {
        const errorMsg = error.response?.data?.error || (
          isComplementoPago ? 'Error al cancelar el complemento de pago' : 'Error al cancelar la factura'
        )
        toast.error(errorMsg)
      }
    }
  )

  const confirmarCancelacionMutation = useMutation(
    async () => {
      const response = await api.post(
        `/financial/invoices/${invoice.id}/confirmar_cancelacion_manual/`
      )
      return response.data
    },
    {
      onSuccess: () => {
        toast.success('Cancelación confirmada manualmente')
        queryClient.invalidateQueries(['invoice', invoice.id])
        queryClient.invalidateQueries({ queryKey: ['invoices'], exact: false })
        if (onRefresh) onRefresh()
      },
      onError: (error) => {
        const errorMsg =
          error.response?.data?.error || 'Error al confirmar la cancelación'
        toast.error(errorMsg)
      },
    }
  )

  const handleConfirmarCancelacionManual = () => {
    const mensaje =
      'Confirmo que verifiqué en el portal del SAT que el CFDI está cancelado. ¿Desea marcar la factura como cancelada en Arithmos?'
    if (window.confirm(mensaje)) {
      confirmarCancelacionMutation.mutate()
    }
  }

  // Mutación para subir PDF validación SAT
  const subirPdfValidacionMutation = useMutation(
    async (file) => {
      const formData = new FormData()
      formData.append('pdf', file)
      const response = await api.post(`/financial/invoices/${invoice.id}/subir_pdf_validacion/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return response.data
    },
    {
      onSuccess: () => {
        toast.success('PDF de validación subido correctamente')
        queryClient.invalidateQueries(['invoice', invoice.id])
        queryClient.invalidateQueries({ queryKey: ['invoices'], exact: false })
        if (onRefresh) onRefresh()
        if (fileInputRef.current) fileInputRef.current.value = ''
      },
      onError: (error) => {
        const errorMsg = error.response?.data?.error || 'Error al subir PDF'
        toast.error(errorMsg)
      }
    }
  )

  // Mutación para revertir timbrado (solo PAC pruebas)
  const revertirTimbradoMutation = useMutation(
    async () => {
      const response = await api.post(`/financial/invoices/${invoice.id}/revertir-timbrado-pruebas/`)
      return response.data
    },
    {
      onSuccess: async () => {
        toast.success('Timbrado revertido. Puede editar y timbrar nuevamente.')
        await queryClient.invalidateQueries(['invoice', invoice.id])
        await queryClient.invalidateQueries({ queryKey: ['invoices'], exact: false })
        if (onRefresh) await onRefresh()
      },
      onError: (error) => {
        const errorMsg = error.response?.data?.error || 'Error al revertir timbrado'
        toast.error(errorMsg)
      }
    }
  )

  // Mutación para reenviar email
  const reenviarEmailMutation = useMutation(
    async () => {
      const response = await api.post(`/financial/invoices/${invoice.id}/reenviar_email/`)
      return response.data
    },
    {
      onSuccess: () => {
        toast.success('Envío de email encolado')
        queryClient.invalidateQueries(['invoice', invoice.id])
        queryClient.invalidateQueries({ queryKey: ['invoices'], exact: false })
        if (onRefresh) onRefresh()
      },
      onError: (error) => {
        const errorMsg = error.response?.data?.error || 'Error al encolar envío de email'
        toast.error(errorMsg)
      }
    }
  )

  // Mutación para procesar contablemente (tras revisar asiento)
  const [showAsientoModal, setShowAsientoModal] = useState(false)
  const [asientoPropuesta, setAsientoPropuesta] = useState(null)
  const [asientoPreviewError, setAsientoPreviewError] = useState(null)
  const [loadingAsientoPreview, setLoadingAsientoPreview] = useState(false)

  const openAsientoPreview = async () => {
    setShowAsientoModal(true)
    setLoadingAsientoPreview(true)
    setAsientoPreviewError(null)
    setAsientoPropuesta(null)
    try {
      const res = await api.get(`/financial/invoices/${invoice.id}/preview-asiento-contable/`)
      setAsientoPropuesta(res.data)
    } catch (error) {
      setAsientoPreviewError(error.response?.data?.error || 'No se pudo cargar la propuesta contable')
    } finally {
      setLoadingAsientoPreview(false)
    }
  }

  const procesarContableMutation = useMutation(
    async ({ lineas_contables } = {}) => {
      const response = await api.post(`/financial/invoices/${invoice.id}/procesar_contable/`, {
        lineas_contables: lineas_contables || [],
      })
      return response.data
    },
    {
      onSuccess: () => {
        toast.success('Factura procesada contablemente')
        queryClient.invalidateQueries(['invoice', invoice.id])
        queryClient.invalidateQueries({ queryKey: ['invoices'], exact: false })
        queryClient.invalidateQueries({ queryKey: ['invoices-stats-procesamiento'], exact: false })
        setShowAsientoModal(false)
        if (onRefresh) onRefresh()
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al procesar contablemente')
      }
    }
  )

  // Mutación para revertir proceso contable
  const revertirProcesoContableMutation = useMutation(
    async () => {
      const response = await api.post(`/financial/invoices/${invoice.id}/revertir_proceso_contable/`)
      return response.data
    },
    {
      onSuccess: () => {
        toast.success('Proceso contable revertido correctamente')
        queryClient.invalidateQueries(['invoice', invoice.id])
        queryClient.invalidateQueries({ queryKey: ['invoices'], exact: false })
        queryClient.invalidateQueries({ queryKey: ['invoices-stats-procesamiento'], exact: false })
        if (onRefresh) onRefresh()
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'No se puede revertir si la factura tiene pagos aplicados.')
      }
    }
  )

  const actualizarCuentaConceptoMutation = useMutation(
    async ({ conceptoId, cuentaContableId }) => {
      const response = await api.post(`/financial/invoices/${invoice.id}/actualizar_cuenta_concepto/`, {
        concepto_id: conceptoId,
        cuenta_contable_id: cuentaContableId
      })
      return response.data
    },
    {
      onSuccess: (data) => {
        toast.success('Cuenta contable actualizada')
        queryClient.invalidateQueries(['invoice', invoice.id])
        queryClient.invalidateQueries({ queryKey: ['invoices'], exact: false })
        if (onInvoiceUpdated && data?.concepto) {
          const updatedConceptos = (invoice.conceptos || []).map(c =>
            c.id === data.concepto.id ? data.concepto : c
          )
          onInvoiceUpdated({ ...invoice, conceptos: updatedConceptos })
        }
        cerrarModalCuenta()
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al actualizar cuenta')
      }
    }
  )

  const abrirModalCuenta = (concepto) => {
    setConceptoSeleccionadoCuenta(concepto)
    setMostrarModalCuenta(true)
  }

  const cerrarModalCuenta = () => {
    setMostrarModalCuenta(false)
    setConceptoSeleccionadoCuenta(null)
  }

  const seleccionarCuenta = (cuenta) => {
    if (!conceptoSeleccionadoCuenta || !cuenta?.id) {
      toast.error('Error al seleccionar cuenta')
      return
    }
    const realId = (cuenta.cuenta_base_id != null && cuenta.cuenta_base_id !== '')
      ? cuenta.cuenta_base_id
      : (typeof cuenta.id === 'number' ? cuenta.id : parseInt(cuenta.id, 10))
    actualizarCuentaConceptoMutation.mutate({
      conceptoId: conceptoSeleccionadoCuenta.id,
      cuentaContableId: realId
    })
  }

  const handleTimbrar = () => {
    if (window.confirm('¿Está seguro de que desea timbrar esta factura?')) {
      timbrarMutation.mutate()
    }
  }

  // Cliente emisor: puede venir como objeto o como id
  const clientId = typeof invoice.client === 'object' ? invoice.client?.id : (invoice.client || invoice.client_id)
  
  // Configuraciones PAC del cliente emisor (para leyenda de timbrado)
  const { data: configuracionesPAC } = useQuery(
    ['pac-config', clientId],
    async () => {
      if (!clientId) return []
      const response = await api.get(`/financial/configuraciones-pac/?cliente_id=${clientId}&activo=true`)
      return response.data.results || response.data || []
    },
    { enabled: !!clientId }
  )
  
  const configuracionPACPredeterminada = configuracionesPAC?.find(c => c.es_predeterminado && c.activo) ||
    configuracionesPAC?.find(c => c.activo) || null
  
  const TIPO_PAC_LABELS = {
    facturacion_moderna: 'Facturación Moderna (FINKOK)',
    solucion_factible: 'Solución Factible',
    sw: 'SW Soluciones en Informática',
    multifactura: 'MultiFactura',
    edicom: 'Edicom',
    otro: 'Otro'
  }
  
  const openCancelModal = async () => {
    setCancelMotivo('02')
    setCancelFolioSustitucion('')
    setCancelNota('')
    setCancelAuthAdmin(false)
    setCancelErrors({})
    setCancelRepsInfo([])
    setShowCancelModal(true)
    try {
      const { data } = await api.get(`/financial/invoices/${invoice.id}/resumen-nota-credito/`)
      const reps = (data?.reps_relacionados || []).filter(
        (r) => r.timbrado && (r.status || '').toUpperCase() !== 'CANCELLED'
      )
      setCancelRepsInfo(reps)
    } catch {
      setCancelRepsInfo([])
    }
  }

  const handleCancelar = () => {
    const errs = {}
    if (!cancelMotivo) errs.motivo = 'Seleccione un motivo de cancelación'
    const folioTrim = (cancelFolioSustitucion || '').trim()
    if (cancelMotivo === '01') {
      if (!folioTrim) {
        errs.folio_sustitucion = 'El folio fiscal sustitución (UUID) es obligatorio para motivo 01'
      } else if (!UUID_RE.test(folioTrim)) {
        errs.folio_sustitucion = 'UUID inválido. Use formato XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX'
      }
    }
    if (cancelMotivo === '03' && (cancelNota || '').trim().length < 10) {
      errs.nota = 'Documente que la operación no se realizó (mínimo 10 caracteres)'
    }
    if (cancelRepsInfo.length > 0) {
      errs.reps = 'Existen REP/complementos timbrados. Cancélelos o revíselos antes de cancelar esta factura.'
    }
    setCancelErrors(errs)
    if (Object.keys(errs).length > 0) return
    cancelarMutation.mutate({
      motivo: cancelMotivo,
      folio_sustitucion: folioTrim,
      nota: cancelNota,
      autorizacion_admin: cancelAuthAdmin,
    })
  }
  
  // Sin usar a proposito: la consulta de estatus ante el PAC esta detras de
  // PAC_CONSULTA_ESTATUS_HABILITADA (settings/base.py), hoy en False. El handler,
  // el modal y el endpoint existen; falta solo conectar el boton cuando se active.
  const _handleConsultarEstatus = async () => {
    try {
      const response = await api.get(`/financial/invoices/${invoice.id}/consultar_estatus/`)
      setEstatusData(response.data)
      setShowEstatusModal(true)
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Error al consultar el estatus'
      toast.error(errorMsg)
    }
  }

  const handleSubirPdfValidacion = (e) => {
    const file = e.target.files?.[0]
    if (file && file.type === 'application/pdf') {
      subirPdfValidacionMutation.mutate(file)
    } else if (file) {
      toast.error('El archivo debe ser un PDF')
    }
  }

  const handleVerDatosValidacion = async () => {
    try {
      const response = await api.get(`/financial/invoices/${invoice.id}/validacion-sat-helper/`)
      const { sat_url } = response.data
      if (sat_url) {
        window.open(sat_url, '_blank', 'noopener,noreferrer')
      } else {
        toast.error('No se pudo obtener la URL de verificación')
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.response?.statusText || 'Error al cargar'
      toast.error(errorMsg)
    }
  }

  
  const handleDescargarXML = async () => {
    try {
      const response = await api.get(`/financial/invoices/${invoice.id}/descargar_xml/`, {
        responseType: 'blob'
      })
      
      const blob = new Blob([response.data], { type: 'application/xml' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = getFilenameFromContentDisposition(
        response,
        buildCfdiDownloadFilename(invoice, 'xml')
      )
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      toast.success('XML descargado exitosamente')
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Error al descargar el XML'
      toast.error(errorMsg)
    }
  }

  const handleVerPdf = async () => {
    setLoadingPdf(true)
    setPdfBlobUrl(null)
    try {
      const response = await api.get(`/financial/invoices/${invoice.id}/descargar_pdf/?inline=1`, {
        responseType: 'blob'
      })
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      setPdfBlobUrl(url)
      setShowPdfModal(true)
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Error al cargar el PDF'
      toast.error(errorMsg)
    } finally {
      setLoadingPdf(false)
    }
  }

  const handleCerrarPdfModal = () => {
    setShowPdfModal(false)
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl)
      setPdfBlobUrl(null)
    }
  }

  const handleDescargarDesdeModal = async () => {
    setDescargando(true)
    try {
      const pdfResponse = await api.get(`/financial/invoices/${invoice.id}/descargar_pdf/`, {
        responseType: 'blob'
      })
      const pdfBlob = new Blob([pdfResponse.data], { type: 'application/pdf' })
      const pdfUrl = URL.createObjectURL(pdfBlob)
      const pdfLink = document.createElement('a')
      pdfLink.href = pdfUrl
      pdfLink.download = getFilenameFromContentDisposition(
        pdfResponse,
        buildCfdiDownloadFilename(invoice, 'pdf')
      )
      document.body.appendChild(pdfLink)
      pdfLink.click()
      document.body.removeChild(pdfLink)
      URL.revokeObjectURL(pdfUrl)

      if (invoice.cfdi_uuid) {
        const xmlResponse = await api.get(`/financial/invoices/${invoice.id}/descargar_xml/`, {
          responseType: 'blob'
        })
        const xmlBlob = new Blob([xmlResponse.data], { type: 'application/xml' })
        const xmlUrl = URL.createObjectURL(xmlBlob)
        const xmlLink = document.createElement('a')
        xmlLink.href = xmlUrl
        xmlLink.download = getFilenameFromContentDisposition(
          xmlResponse,
          buildCfdiDownloadFilename(invoice, 'xml')
        )
        document.body.appendChild(xmlLink)
        xmlLink.click()
        document.body.removeChild(xmlLink)
        URL.revokeObjectURL(xmlUrl)
        toast.success('PDF y XML descargados exitosamente')
      } else {
        toast.success('PDF descargado exitosamente')
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Error al descargar'
      toast.error(errorMsg)
    } finally {
      setDescargando(false)
    }
  }

  useEffect(() => {
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl)
      }
    }
  }, [pdfBlobUrl])
  
  const conceptos = invoice.conceptos || []
  const IMPUESTO_LABEL_BY_CODE = { '001': 'ISR', '002': 'IVA', '003': 'IEPS' }
  const desgloseImpuestosUI = !!invoice.desglose_impuestos_pdf

  const normalizeTasaFrac = (rawTasa) => {
    const t = parseFloat(rawTasa)
    if (!Number.isFinite(t)) return 0
    return Math.abs(t) > 1 ? t / 100 : t
  }

  const pctDisplay = (tasaFrac) => {
    const pct = normalizeTasaFrac(tasaFrac) * 100
    const s = Number(pct.toFixed(4)).toString()
    return `${s}%`
  }

  const parteTrasladoLabel = (code, tipoFactor, tasa) => {
    const name = IMPUESTO_LABEL_BY_CODE[code] || code || '—'
    if (String(tipoFactor || '').toLowerCase() === 'exento') return `${name} EXENTO`
    return `${name} ${pctDisplay(tasa)}`
  }

  const parteRetencionLabel = (code, tasa) => {
    const name = IMPUESTO_LABEL_BY_CODE[code] || code || '—'
    return `${name} ${pctDisplay(tasa)}`
  }

  /** Agrega traslados de un concepto: [{ key, label, importe }] */
  const collectTrasladosConcepto = (c) => {
    const map = new Map()
    const rows = Array.isArray(c.impuestos_trasladados) ? c.impuestos_trasladados : []
    if (rows.length > 0) {
      rows.forEach((r) => {
        const code = (r?.impuesto || '002').toString().trim() || '002'
        const tf = (r?.tipo_factor || 'Tasa').toString().trim() || 'Tasa'
        const isExento = tf.toLowerCase() === 'exento'
        const tasa = isExento ? 0 : normalizeTasaFrac(r?.tasa)
        const key = isExento ? `${code}|EXENTO` : `${code}|TASA|${tasa}`
        const importe = parseFloat(r?.importe || 0) || 0
        const prev = map.get(key)
        if (prev) prev.importe += importe
        else map.set(key, { label: parteTrasladoLabel(code, tf, tasa), importe })
      })
    } else {
      const importe = parseFloat(c.iva_trasladado || 0) || 0
      if (importe > 0) {
        const tasa = 0.16
        map.set('002|TASA|0.16', { label: parteTrasladoLabel('002', 'Tasa', tasa), importe })
      }
    }
    return Array.from(map.values())
  }

  /** Agrega retenciones de un concepto: [{ key, label, importe }] */
  const collectRetencionesConcepto = (c) => {
    const map = new Map()
    const rows = Array.isArray(c.retenciones_detalle) ? c.retenciones_detalle : []
    if (rows.length > 0) {
      rows.forEach((r) => {
        const code = (r?.impuesto || '').toString().trim()
        if (!['001', '002', '003'].includes(code)) return
        let importe = parseFloat(r?.importe || 0) || 0
        if (importe <= 0) return
        let tasa = normalizeTasaFrac(r?.tasa)
        if (!tasa) {
          const base = parseFloat(c.importe || 0) || 0
          if (base > 0) tasa = importe / base
        }
        const key = `${code}|${tasa}`
        const prev = map.get(key)
        if (prev) prev.importe += importe
        else map.set(key, { label: parteRetencionLabel(code, tasa), importe })
      })
    } else {
      const importe = parseFloat(c.iva_retenido || 0) || 0
      if (importe > 0) {
        const code = (c.retencion_impuesto || '002').toString().trim() || '002'
        const base = parseFloat(c.importe || 0) || 0
        const tasa = base > 0 ? importe / base : 0
        map.set(`${code}|${tasa}`, { label: parteRetencionLabel(code, tasa), importe })
      }
    }
    return Array.from(map.values())
  }

  const aggregateTaxParts = (conceptosList, collector) => {
    const map = new Map()
    ;(conceptosList || []).forEach((c) => {
      collector(c).forEach((item) => {
        const key = item.label
        const prev = map.get(key)
        if (prev) prev.importe += item.importe
        else map.set(key, { label: item.label, importe: item.importe })
      })
    })
    return Array.from(map.values())
  }

  const renderTaxCell = (items, kind) => {
    const prefix = kind === 'traslado' ? 'Traslados' : 'Retenciones'
    if (!items.length) return <span className="text-muted">{formatCurrency(0)}</span>
    if (desgloseImpuestosUI) {
      return (
        <div className="small">
          {items.map((item) => (
            <div key={`${prefix}-${item.label}`}>
              {prefix} ({item.label}): {formatCurrency(item.importe)}
            </div>
          ))}
        </div>
      )
    }
    const sum = items.reduce((s, x) => s + (x.importe || 0), 0)
    const parts = items.map((x) => x.label).join(', ')
    return (
      <div className="small">
        <div>{prefix} ({parts})</div>
        <div>{formatCurrency(sum)}</div>
      </div>
    )
  }

  const trasladosTotalesParts = aggregateTaxParts(conceptos, collectTrasladosConcepto)
  const retencionesTotalesParts = aggregateTaxParts(conceptos, collectRetencionesConcepto)
  const descuentoTotal = parseFloat(invoice.descuento_total || 0) || 0
  const aplicaciones = invoice.aplicaciones_pago || []
  const xmlData = invoice.xml_data || {}
  const isTimbrado = !!invoice.cfdi_uuid

  // Helper para filtrar valores "N/A"
  const filterNA = (value) => {
    if (!value || value === 'N/A' || value === 'null' || value === null || value === undefined) {
      return ''
    }
    return String(value).trim()
  }

  const splitEmailList = (value) => {
    const clean = filterNA(value)
    if (!clean) return []
    return clean
      .split(',')
      .map((email) => email.trim())
      .filter(Boolean)
  }

  // Obtener datos del emisor (filtrados)
  const emisorName = filterNA(invoice.client_name) || 'N/A'
  const emisorRFC = filterNA(invoice.client_rfc) || 'N/A'
  const emisorDireccion = filterNA(invoice.client_direccion) || 'N/A'
  const emisorEmails = splitEmailList(invoice.client_email)
  // Obtener datos del receptor (filtrados)
  const receptorName = filterNA(invoice.receptor_name) || filterNA(invoice.receptor_razon_social) || 'N/A'
  const receptorRFC = filterNA(invoice.receptor_rfc_display) || filterNA(invoice.receptor_rfc) || 'N/A'
  const receptorDireccion = filterNA(invoice.receptor_direccion_display) || filterNA(invoice.receptor_direccion) || 'N/A'
  const receptorCodigoPostal = filterNA(invoice.receptor_codigo_postal)
  const receptorEmails = splitEmailList(invoice.receptor_email)
  const receptorRegimenFiscal = invoice.receptor_regimen_fiscal_display
  const isPublicoExtranjero = (receptorRFC || '').toString().trim().toUpperCase() === 'XEXX010101000'

  const folio = getDisplaySerieFolio(invoice.invoice_number)

  // Obtener uso CFDI legible
  const getUsoCFDILabel = (codigo) => {
    const usos = {
      'G01': 'G01 - Adquisición de mercancías',
      'G02': 'G02 - Devoluciones, descuentos o bonificaciones',
      'G03': 'G03 - Gastos en general',
      'I01': 'I01 - Construcciones',
      'I02': 'I02 - Mobiliario y equipo de oficina por inversiones',
      'I03': 'I03 - Equipo de transporte',
      'I04': 'I04 - Equipo de computo y accesorios',
      'I05': 'I05 - Dados, troqueles, moldes, matrices y herramental',
      'I06': 'I06 - Comunicaciones telefónicas',
      'I07': 'I07 - Comunicaciones satelitales',
      'I08': 'I08 - Otra maquinaria y equipo',
      'D01': 'D01 - Honorarios médicos, dentales y gastos hospitalarios',
      'D02': 'D02 - Gastos médicos por incapacidad o discapacidad',
      'D03': 'D03 - Gastos funerales',
      'D04': 'D04 - Donativos',
      'D05': 'D05 - Intereses reales efectivamente pagados por créditos hipotecarios (casa habitación)',
      'D06': 'D06 - Aportaciones voluntarias al SAR',
      'D07': 'D07 - Primas por seguros de gastos médicos',
      'D08': 'D08 - Gastos de transportación escolar obligatoria',
      'D09': 'D09 - Depósitos en cuentas para el ahorro, primas que tengan como base planes de pensiones',
      'D10': 'D10 - Pagos por servicios educativos (colegiaturas)',
      'S01': 'S01 - Sin efectos fiscales',
      'CP01': 'CP01 - Pagos',
      'CN01': 'CN01 - Nómina'
    }
    return usos[codigo] || codigo || 'No especificado'
  }

  const getStatusBadge = (status) => {
    const badge = getInvoiceStatusBadge(status)
    return <Badge bg={badge.bg} title="Estado de la factura (flujo de cobranza/pago)">{badge.text}</Badge>
  }

  const getTypeBadge = (type) => {
    const badges = {
      'normal': { bg: 'primary', text: 'Normal' },
      'global': { bg: 'info', text: 'Global' },
      'complemento': { bg: 'warning', text: 'Complemento' }
    }
    const badge = badges[type] || { bg: 'secondary', text: type }
    return <Badge bg={badge.bg}>{badge.text}</Badge>
  }

  const getFormaPagoLabel = (codigo) => {
    const map = {
      '01': '01 - Efectivo', '02': '02 - Cheque nominativo', '03': '03 - Transferencia electrónica de fondos',
      '04': '04 - Tarjeta de crédito', '05': '05 - Monedero electrónico', '06': '06 - Dinero electrónico',
      '08': '08 - Vales de despensa', '12': '12 - Dación en pago', '13': '13 - Pago por subrogación',
      '14': '14 - Pago por consignación', '15': '15 - Condonación', '17': '17 - Compensación',
      '23': '23 - Novación', '24': '24 - Confusión', '25': '25 - Remisión de deuda',
      '26': '26 - Prescripción o caducidad', '27': '27 - A satisfacción del acreedor',
      '28': '28 - Tarjeta de débito', '29': '29 - Tarjeta de servicios', '30': '30 - Aplicación de anticipos',
      '31': '31 - Intermediario pagos', '99': '99 - Por definir'
    }
    return map[codigo] || codigo || '03 - Transferencia electrónica de fondos'
  }

  const getMetodoPagoLabel = (codigo) => {
    const map = { 'PUE': 'PUE - Pago en una sola exhibición', 'PPD': 'PPD - Pago en parcialidades o diferido' }
    return map[codigo] || codigo || 'PUE - Pago en una sola exhibición'
  }

  const getExportacionLabel = (codigo) => {
    const map = {
      '01': '01 - No aplica', '02': '02 - Definitiva con clave A1',
      '03': '03 - Temporal', '04': '04 - Definitiva distinta a A1 / sin enajenación CFF'
    }
    return map[codigo] || codigo || '01 - No aplica'
  }

  const emisorRegimenFiscalDisplay = invoice.emisor_regimen_fiscal_display || invoice.client_regimen_fiscal

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4>Factura {getDisplaySerieFolio(invoice.invoice_number)}</h4>
          <div className="mt-2">
            <span className="text-muted small me-2">Tipo:</span>{getTypeBadge(invoice.tipo_factura)}
            <span className="text-muted small ms-3 me-2">Estado factura:</span>{getStatusBadge(invoice.status)}
          </div>
          {/* Un borrador de recurrencia no tiene folio todavía: el oficial se toma del
              consecutivo al timbrar. Decirlo aquí evita que el contador crea que ya gastó un
              número de su serie --y que la fecha futura sea un error--. */}
          {esFolioProvisional(invoice.invoice_number) && (
            <Alert variant="info" className="mt-2 py-2 small mb-0">
              <strong>Este borrador todavía no tiene folio.</strong> Su folio oficial y consecutivo
              se genera <strong>al timbrarlo</strong>, así que el consecutivo de la serie no se ha
              movido. La fecha de emisión es la de su periodo
              {periodoDeFolioProvisional(invoice.invoice_number)
                ? ` (${periodoDeFolioProvisional(invoice.invoice_number)})`
                : ''} y se ajustará al emitir.
            </Alert>
          )}
        </div>
        <div className="d-flex gap-2 flex-wrap align-items-center">
          {(!invoice.cfdi_uuid || isInvoiceCancelled(invoice.status)) && (
            <Button 
              variant="success" 
              size="sm" 
              onClick={handleTimbrar}
              disabled={timbrarMutation.isLoading}
              style={{ minWidth: '110px' }}
            >
              {timbrarMutation.isLoading ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  Timbrando...
                </>
              ) : (
                <>
                  <i className="bi bi-stamp me-1"></i>
                  Timbrar
                </>
              )}
            </Button>
          )}
          {invoice.cfdi_uuid && (
            <Button 
              variant="outline-info" 
              size="sm" 
              onClick={handleVerDatosValidacion}
              title="Abrir portal SAT y validar el CFDI (campos pre-llenados)"
              style={{ minWidth: '110px' }}
            >
              <i className="bi bi-check2-square me-1"></i>
              Validar
            </Button>
          )}
          {isInvoiceTimbradaVigente(invoice) && (
            <>
              <Button 
                variant="outline-success" 
                size="sm" 
                onClick={handleDescargarXML}
                style={{ minWidth: '130px' }}
              >
                <i className="bi bi-download me-1"></i>
                Descargar XML
              </Button>
              {canRequestCancelacion(invoice.status) && (
                <Button 
                  variant="danger" 
                  size="sm" 
                  onClick={openCancelModal}
                  style={{ minWidth: '110px' }}
                >
                  <i className="bi bi-x-circle me-1"></i>
                  {isComplementoPago ? 'Cancelar REP' : 'Cancelar'}
                </Button>
              )}
              {isInvoiceCancelPending(invoice.status) && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleConfirmarCancelacionManual}
                  disabled={confirmarCancelacionMutation.isLoading}
                  title="Confirmar que el CFDI ya está cancelado en el SAT"
                  style={{ minWidth: '200px' }}
                >
                  {confirmarCancelacionMutation.isLoading ? (
                    <><Spinner size="sm" className="me-1" /> Confirmando...</>
                  ) : (
                    <><i className="bi bi-check-circle me-1"></i> Confirmar cancelación</>
                  )}
                </Button>
              )}
              {invoice.ambiente_timbrado === 'pruebas' && (
                <Button 
                  variant="outline-secondary" 
                  size="sm" 
                  onClick={() => window.confirm('¿Revertir el timbrado? Podrá editar y timbrar nuevamente (por ejemplo en producción).') && revertirTimbradoMutation.mutate()}
                  disabled={revertirTimbradoMutation.isLoading}
                  title="Revertir timbrado para poder editar y volver a timbrar"
                  style={{ minWidth: '150px' }}
                >
                  {revertirTimbradoMutation.isLoading ? (
                    <><Spinner size="sm" className="me-1" /> Revertiendo...</>
                  ) : (
                    <><i className="bi bi-arrow-counterclockwise me-1"></i> Volver a timbrar</>
                  )}
                </Button>
              )}
              <Button 
                variant="outline-warning" 
                size="sm" 
                onClick={() => reenviarEmailMutation.mutate()}
                disabled={reenviarEmailMutation.isLoading}
                title={invoice.estado_email === 'error' ? 'Reintentar envío de factura por email' : 'Enviar factura por email al receptor'}
                style={{ minWidth: '150px' }}
              >
                {reenviarEmailMutation.isLoading ? (
                  <><Spinner size="sm" className="me-1" /> Encolando...</>
                ) : invoice.estado_email === 'error' ? (
                  <><i className="bi bi-envelope-arrow-up me-1"></i> Reintentar envío</>
                ) : (
                  <><i className="bi bi-envelope me-1"></i> Enviar por email</>
                )}
              </Button>
              {invoice.tipo_factura === 'normal' && invoice.status !== 'DRAFT' && !isInvoiceCancelled(invoice.status) && !invoice.is_processed_contablemente && (
                <Button
                  variant="outline-info"
                  size="sm"
                  onClick={openAsientoPreview}
                  disabled={procesarContableMutation.isLoading || !invoice.client}
                  title={!invoice.client ? 'Asigna un cliente para procesar' : 'Revisar y procesar contablemente'}
                  style={{ minWidth: '130px' }}
                >
                  {procesarContableMutation.isLoading ? <><Spinner size="sm" className="me-1" /> Procesando...</> : <><i className="bi bi-calculator me-1"></i> Procesar contablemente</>}
                </Button>
              )}
              {invoice.tipo_factura === 'normal' && invoice.is_processed_contablemente && (
                <Button
                  variant="outline-warning"
                  size="sm"
                  onClick={() => {
                    if (window.confirm('¿Revertir proceso contable? No se puede revertir si la factura tiene pagos aplicados.')) {
                      revertirProcesoContableMutation.mutate()
                    }
                  }}
                  disabled={revertirProcesoContableMutation.isLoading}
                  title="Revertir proceso contable. No se puede si tiene pagos aplicados."
                  style={{ minWidth: '160px' }}
                >
                  {revertirProcesoContableMutation.isLoading ? <><Spinner size="sm" className="me-1" /> Revirtiendo...</> : <><i className="bi bi-arrow-counterclockwise me-1"></i> Revertir Proceso Contable</>}
                </Button>
              )}
            </>
          )}
          {(!isTimbrado || isInvoiceCancelled(invoice.status)) && (
            <Button 
              variant="outline-primary" 
              size="sm" 
              onClick={onEdit}
              title="Editar factura"
              style={{ minWidth: '110px' }}
            >
              <i className="bi bi-pencil me-1"></i>
              Editar
            </Button>
          )}
          <Button 
            variant="outline-secondary" 
            size="sm" 
            onClick={handleVerPdf}
            disabled={loadingPdf}
            title="Ver PDF de la factura"
            style={{ minWidth: '110px' }}
          >
            {loadingPdf ? (
              <><Spinner size="sm" className="me-1" /> Cargando...</>
            ) : (
              <><i className="bi bi-file-pdf me-1"></i> Ver PDF</>
            )}
          </Button>
        </div>
      </div>

      {isInvoiceCancelPending(invoice.status) && (
        <Alert variant="warning" className="py-2 mb-3">
          <small>
            <strong>Cancelación en proceso:</strong> la solicitud fue enviada al PAC pero aún no está
            confirmada como cancelada en el SAT. Use el botón <strong>Validar</strong> en el portal del SAT
            y, si ya aparece cancelada, pulse <strong>Confirmar cancelación</strong>.
          </small>
        </Alert>
      )}

      {(!invoice.cfdi_uuid || isInvoiceCancelled(invoice.status)) && invoice.issue_date_ventana_mensaje && (
        <Alert variant="warning" className="py-2 mb-3">
          <small>
            <strong>Advertencia:</strong> {invoice.issue_date_ventana_mensaje}
          </small>
        </Alert>
      )}

      {/* Leyenda de tipo de timbrado - cuando se puede timbrar (borrador o cancelada) */}
      {(!invoice.cfdi_uuid || isInvoiceCancelled(invoice.status)) && (
        <div className="mb-3">
          {configuracionPACPredeterminada ? (
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
              <small className="text-muted">
                <strong>El timbrado se realizará con:</strong>{' '}
                {TIPO_PAC_LABELS[configuracionPACPredeterminada.tipo_pac] || configuracionPACPredeterminada.tipo_pac}
                {' - '}{configuracionPACPredeterminada.nombre}
                {' '}(Ambiente: {configuracionPACPredeterminada.ambiente === 'produccion' ? 'Producción' : 'Pruebas'})
              </small>
              <Form.Check
                type="checkbox"
                id={`desglose-impuestos-pdf-detail-${invoice.id}`}
                className="ms-auto mb-0"
                label={<small>Desglose de impuestos</small>}
                checked={!!invoice.desglose_impuestos_pdf}
                disabled={desgloseImpuestosMutation.isLoading}
                onChange={(e) => desgloseImpuestosMutation.mutate(e.target.checked)}
              />
            </div>
          ) : clientId ? (
            <Alert variant="warning" className="py-2 mb-0">
              <small>
                <strong>Advertencia:</strong> Este cliente no tiene configuración PAC. Configure una configuración PAC para poder timbrar facturas.
              </small>
            </Alert>
          ) : (
            <small className="text-muted">
              Seleccione un cliente emisor para ver la configuración PAC de timbrado.
            </small>
          )}
        </div>
      )}

      {/* Checkbox visible también en facturas ya timbradas (solo afecta regeneración PDF) */}
      {invoice.cfdi_uuid && !isInvoiceCancelled(invoice.status) && (
        <div className="mb-3 d-flex justify-content-end">
          <Form.Check
            type="checkbox"
            id={`desglose-impuestos-pdf-detail-timbrada-${invoice.id}`}
            className="mb-0"
            label={<small>Desglose de impuestos (PDF)</small>}
            checked={!!invoice.desglose_impuestos_pdf}
            disabled={desgloseImpuestosMutation.isLoading}
            onChange={(e) => desgloseImpuestosMutation.mutate(e.target.checked)}
          />
        </div>
      )}

      {/* Información General de la Factura */}
      <Card className="mb-3">
        <Card.Header>
          <h5 className="mb-0">Información General</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <p>
                <strong>Serie:</strong> {invoice.configuracion_serie || 'Sin serie'}
                {invoice.configuracion_activa === false && (
                  <Badge bg="secondary" className="ms-2" title="Serie histórica; no disponible para nuevas emisiones">
                    Inactiva
                  </Badge>
                )}
              </p>
              <p><strong>Folio:</strong> {folio}</p>
              <p><strong>Tipo de Factura:</strong> {getTypeBadge(invoice.tipo_factura)}</p>
              {invoice.tipo_factura === 'global' && (
                <>
                  <p><strong>Periodicidad (SAT):</strong> {(
                    { '01': 'Diario', '02': 'Semanal', '03': 'Quincenal', '04': 'Mensual', '05': 'Bimestral' }
                  )[invoice.global_periodicidad] || invoice.global_periodicidad || '—'}</p>
                  <p><strong>Mes (SAT):</strong> {(
                    {
                      '01': 'Enero', '02': 'Febrero', '03': 'Marzo', '04': 'Abril', '05': 'Mayo', '06': 'Junio',
                      '07': 'Julio', '08': 'Agosto', '09': 'Septiembre', '10': 'Octubre', '11': 'Noviembre', '12': 'Diciembre',
                      '13': 'Enero-Febrero', '14': 'Marzo-Abril', '15': 'Mayo-Junio', '16': 'Julio-Agosto',
                      '17': 'Septiembre-Octubre', '18': 'Noviembre-Diciembre',
                    }
                  )[invoice.global_meses] || invoice.global_meses || '—'}</p>
                  <p><strong>Año:</strong> {invoice.global_anio ?? '—'}</p>
                </>
              )}
              <p><strong>Título (PDF):</strong> {invoice.titulo_pdf || <span className="text-muted">—</span>}</p>
              <p><strong>Forma de pago:</strong> {getFormaPagoLabel(invoice.forma_pago)}</p>
              <p><strong>Método de pago:</strong> {getMetodoPagoLabel(invoice.metodo_pago)}</p>
            </Col>
            <Col md={6}>
              <p><strong>Fecha de Emisión:</strong> {formatDateOnlyEsMX(invoice.issue_date, {}, '—')}</p>
              <p><strong>Fecha de Vencimiento:</strong> {formatDateOnlyEsMX(invoice.due_date, {}, '—')}</p>
              <p><strong>Uso CFDI:</strong> {getUsoCFDILabel(invoice.uso_cfdi)}</p>
              <p><strong>Condiciones de pago:</strong> {invoice.condiciones_pago || '—'}</p>
              <p><strong>Origen:</strong> {invoice.origen === 'manual' ? 'Manual' : (invoice.origen === 'xml_importado' ? 'Importado desde XML' : 'Sistema Externo')}</p>
              <p><strong>Exportación (SAT):</strong> {getExportacionLabel(invoice.exportacion)}</p>
              {isTimbrado && (invoice.estado_email || invoice.fecha_email_enviado || invoice.email_error_log) && (
                <div className="mt-2">
                  <p className="mb-1"><strong>Correo electrónico (XML/PDF) al receptor:</strong>{' '}
                    {invoice.estado_email === 'enviado' && <Badge bg="success">Enviado</Badge>}
                    {invoice.estado_email === 'pendiente' && <Badge bg="secondary">Pendiente</Badge>}
                    {invoice.estado_email === 'error' && <Badge bg="danger">Error</Badge>}
                  </p>
                  {invoice.fecha_email_enviado && (
                    <p className="mb-1 text-muted small">Enviado: {formatDateTimeEsMX(invoice.fecha_email_enviado, undefined, '—')}</p>
                  )}
                  {invoice.estado_email === 'error' && invoice.email_error_log && (
                    <Alert variant="danger" className="py-2 small mb-0">{invoice.email_error_log}</Alert>
                  )}
                </div>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Emisor */}
      <Card className="mb-3">
        <Card.Header>
          <h5 className="mb-0">Emisor</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <p><strong>Razón Social:</strong> {emisorName}</p>
              <p><strong>RFC:</strong> {emisorRFC}</p>
              {emisorRegimenFiscalDisplay && (
                <p><strong>Régimen Fiscal:</strong> {emisorRegimenFiscalDisplay.codigo} - {emisorRegimenFiscalDisplay.descripcion}</p>
              )}
            </Col>
            <Col md={6}>
              <p><strong>Dirección:</strong> {emisorDireccion}</p>
              {emisorEmails.length > 0 && (
                <p><strong>Email(s):</strong> {emisorEmails.join(', ')}</p>
              )}
              {invoice.client_codigo_postal && (
                <p><strong>Código Postal:</strong> {invoice.client_codigo_postal}</p>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Receptor */}
      <Card className="mb-3">
        <Card.Header>
          <h5 className="mb-0">Receptor</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <p><strong>Razón Social:</strong> {receptorName}</p>
              {invoice.nombre_receptor_pdf && String(invoice.nombre_receptor_pdf).trim() && (
                <p><strong>Nombre Receptor:</strong> {String(invoice.nombre_receptor_pdf).trim()}</p>
              )}
              <p><strong>RFC:</strong> {receptorRFC}</p>
              {isPublicoExtranjero && (
                <>
                  <p>
                    <strong>Residencia Fiscal:</strong>{' '}
                    {filterNA(invoice.receptor_residencia_fiscal) || 'N/A'}
                  </p>
                  <p>
                    <strong>Número de Identificación Fiscal:</strong>{' '}
                    {filterNA(invoice.receptor_num_reg_id_trib) || 'N/A'}
                  </p>
                  <p>
                    <strong>Código Postal:</strong> {receptorCodigoPostal || 'N/A'}
                  </p>
                </>
              )}
              {receptorRegimenFiscal && (
                <p>
                  <strong>Régimen Fiscal:</strong> {receptorRegimenFiscal.codigo} - {receptorRegimenFiscal.descripcion}
                </p>
              )}
            </Col>
            <Col md={6}>
              <p><strong>Dirección:</strong> {receptorDireccion}</p>
              {!isPublicoExtranjero && receptorCodigoPostal && (
                <p><strong>Código Postal:</strong> {receptorCodigoPostal}</p>
              )}
              {receptorEmails.length > 0 && (
                <p><strong>Email(s):</strong> {receptorEmails.join(', ')}</p>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Facturas relacionadas - solo si hay datos */}
      {invoice.cfdis_relacionados?.length > 0 && (
        <Card className="mb-3">
          <Card.Header>
            <h5 className="mb-0">Facturas relacionadas</h5>
          </Card.Header>
          <Card.Body>
            <ul className="mb-0 ps-3">
              {invoice.cfdis_relacionados.map((rel, idx) => {
                const tipoDesc = {
                  '01': 'Nota de crédito', '02': 'Nota de débito', '03': 'Devolución de mercancía',
                  '04': 'Sustitución de CFDI previos', '05': 'Traslados facturados previamente',
                  '06': 'Factura por traslados previos', '07': 'CFDI por aplicación de anticipo',
                }[rel.tipo_relacion] || `Tipo ${rel.tipo_relacion || '01'}`
                return (
                  <li key={idx} className="mb-1">
                    <strong>UUID:</strong> {rel.uuid || '-'} <span className="text-muted">({rel.tipo_relacion || '01'} - {tipoDesc})</span>
                  </li>
                )
              })}
            </ul>
          </Card.Body>
        </Card>
      )}

      {/* Complemento de pago: facturas originales / DoctoRelacionado */}
      {isComplementoPago && (
        <Card className="mb-3">
          <Card.Header>
            <h5 className="mb-0">Facturas documentadas (CFDI original / DR)</h5>
          </Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Col md={4}>
                <p className="mb-1">
                  <strong>Fecha de pago:</strong>{' '}
                  {invoice.complemento_fecha_pago
                    ? formatDateOnlyEsMX(invoice.complemento_fecha_pago)
                    : '—'}
                </p>
              </Col>
              <Col md={4}>
                <p className="mb-1">
                  <strong>Forma de pago:</strong>{' '}
                  {invoice.forma_pago || '—'}
                </p>
              </Col>
              <Col md={4}>
                <p className="mb-1">
                  <strong>Referencia / NumOperacion:</strong>{' '}
                  {invoice.complemento_referencia || '—'}
                </p>
              </Col>
            </Row>
            {(invoice.aplicaciones_documentadas || []).length > 0 ? (
              <Table responsive size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Folio</th>
                    <th>UUID</th>
                    <th>Receptor</th>
                    <th>Parc.</th>
                    <th className="text-end">Saldo ant.</th>
                    <th className="text-end">Imp. pagado</th>
                    <th className="text-end">Saldo insol.</th>
                  </tr>
                </thead>
                <tbody>
                  {(invoice.aplicaciones_documentadas || []).map((doc) => {
                    const folioLabel = [doc.serie, doc.folio].filter(Boolean).join('-')
                      || doc.documento
                      || (doc.factura_id ? `#${doc.factura_id}` : '—')
                    return (
                      <tr key={doc.id}>
                        <td><strong>{folioLabel}</strong></td>
                        <td>
                          <small className="font-monospace">
                            {doc.uuid || '—'}
                          </small>
                        </td>
                        <td>{doc.receptor || '—'}</td>
                        <td>{doc.num_parcialidad || 1}</td>
                        <td className="text-end">
                          {doc.imp_saldo_ant !== '' && doc.imp_saldo_ant != null
                            ? formatCurrency(doc.imp_saldo_ant)
                            : '—'}
                        </td>
                        <td className="text-end">
                          {formatCurrency(doc.imp_pagado || doc.monto || 0)}
                        </td>
                        <td className="text-end">
                          {doc.imp_saldo_insoluto !== '' && doc.imp_saldo_insoluto != null
                            ? formatCurrency(doc.imp_saldo_insoluto)
                            : '—'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            ) : (
              <Alert variant="secondary" className="mb-0">
                Este complemento no tiene facturas documentadas vinculadas (aplicaciones).
              </Alert>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Leyendas fiscales - solo si hay datos */}
      {invoice.leyendas_fiscales?.length > 0 && (
        <Card className="mb-3">
          <Card.Header>
            <h5 className="mb-0">Leyendas fiscales</h5>
          </Card.Header>
          <Card.Body>
            <ul className="mb-0 ps-3">
              {invoice.leyendas_fiscales.map((ley, idx) => {
                const texto = typeof ley === 'string' ? ley : (ley.textoLeyenda || [ley.disposicionFiscal, ley.norma].filter(Boolean).join(' - '))
                return <li key={idx} className="mb-1">{texto || '-'}</li>
              })}
            </ul>
          </Card.Body>
        </Card>
      )}

      {/* Conceptos */}
      <Card className="mb-3">
        <Card.Header>
          <h5 className="mb-0">Conceptos</h5>
        </Card.Header>
        <Card.Body>
          {conceptos && conceptos.length > 0 ? (
            <Table responsive>
              <thead>
                <tr>
                  <th>#</th>
                  <th>SKU</th>
                  <th>Cantidad</th>
                  <th>Unidad</th>
                  <th>ClaveUnidad</th>
                  <th>Clave SAT</th>
                  <th>Descripción</th>
                  <th>Precio Unitario</th>
                  <th>Descuento</th>
                  <th>Importe</th>
                  <th>Impuestos</th>
                  <th>Retención</th>
                  <th>Cuenta Contable</th>
                </tr>
              </thead>
              <tbody>
                {conceptos.map((concepto, index) => (
                  <tr key={concepto.id}>
                    <td>{concepto.orden || index + 1}</td>
                    <td>{concepto.no_identificacion || '—'}</td>
                    <td>{parseFloat(concepto.cantidad || 0).toLocaleString('es-MX')}</td>
                    <td>{concepto.unidad_medida || 'UNI'}</td>
                    <td>{concepto.clave_unidad || '—'}</td>
                    <td>
                      {concepto.clave_producto_servicio ? (
                        <Badge bg="info">{concepto.clave_producto_servicio}</Badge>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>{concepto.descripcion}</td>
                    <td>
                      {formatCurrency(concepto.precio_unitario || 0)}
                    </td>
                    <td>
                      {formatCurrency(concepto.descuento || 0)}
                    </td>
                    <td>
                      {formatCurrency(concepto.importe || 0)}
                    </td>
                    <td>
                      {renderTaxCell(collectTrasladosConcepto(concepto), 'traslado')}
                    </td>
                    <td>
                      {renderTaxCell(collectRetencionesConcepto(concepto), 'retencion')}
                    </td>
                    <td>
                      {invoice.is_processed_contablemente ? (
                        concepto.cuenta_contable_code ? (
                          <Badge bg="success" title={concepto.cuenta_contable_name || ''}>
                            {concepto.cuenta_contable_code}
                          </Badge>
                        ) : (
                          <span className="text-muted">-</span>
                        )
                      ) : (
                        <Button
                          variant={concepto.cuenta_contable_code ? 'outline-success' : 'outline-warning'}
                          size="sm"
                          onClick={() => abrirModalCuenta(concepto)}
                          disabled={actualizarCuentaConceptoMutation.isLoading}
                        >
                          {concepto.cuenta_contable_code || 'Asignar'}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p className="text-muted">No hay conceptos registrados</p>
          )}
        </Card.Body>
      </Card>

      {/* Totales */}
      <Card className="mb-3">
        <Card.Body>
          <Row>
            <Col md={6}></Col>
            <Col md={6}>
              <Table>
                <tbody>
                  <tr>
                    <td><strong>Subtotal:</strong></td>
                    <td className="text-end">
                      {formatCurrency(invoice.subtotal || 0)}
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Descuento:</strong></td>
                    <td className="text-end">
                      {formatCurrency(descuentoTotal)}
                    </td>
                  </tr>
                  {desgloseImpuestosUI ? (
                    <>
                      {(trasladosTotalesParts.length
                        ? trasladosTotalesParts
                        : [{ label: null, importe: 0 }]
                      ).map((item) => (
                        <tr key={`tot-tras-${item.label || 'vacio'}`}>
                          <td>
                            <strong>
                              {item.label ? `Traslados (${item.label})` : 'Traslados'}:
                            </strong>
                          </td>
                          <td className="text-end">{formatCurrency(item.importe)}</td>
                        </tr>
                      ))}
                      {(retencionesTotalesParts.length
                        ? retencionesTotalesParts
                        : [{ label: null, importe: 0 }]
                      ).map((item) => (
                        <tr key={`tot-ret-${item.label || 'vacio'}`}>
                          <td>
                            <strong>
                              {item.label ? `Retenciones (${item.label})` : 'Retenciones'}:
                            </strong>
                          </td>
                          <td className="text-end">{formatCurrency(item.importe)}</td>
                        </tr>
                      ))}
                    </>
                  ) : (
                    <>
                      <tr>
                        <td>
                          <strong>
                            {trasladosTotalesParts.length > 0
                              ? `Traslados (${trasladosTotalesParts.map((x) => x.label).join(', ')})`
                              : 'Traslados'}
                            :
                          </strong>
                        </td>
                        <td className="text-end">
                          {formatCurrency(
                            trasladosTotalesParts.reduce((s, x) => s + x.importe, 0)
                              || invoice.tax_amount
                              || 0
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>
                            {retencionesTotalesParts.length > 0
                              ? `Retenciones (${retencionesTotalesParts.map((x) => x.label).join(', ')})`
                              : 'Retenciones'}
                            :
                          </strong>
                        </td>
                        <td className="text-end">
                          {formatCurrency(
                            retencionesTotalesParts.reduce((s, x) => s + x.importe, 0)
                          )}
                        </td>
                      </tr>
                    </>
                  )}
                  <tr>
                    <td><strong>Total:</strong></td>
                    <td className="text-end">
                      <strong>
                        {formatCurrency(invoice.total || 0)}
                      </strong>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Pagado:</strong></td>
                    <td className="text-end">
                      {formatCurrency(invoice.monto_pagado || 0)}
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Pendiente:</strong></td>
                    <td className="text-end">
                      <strong className="text-warning">
                        {formatCurrency(invoice.monto_pendiente || 0)}
                      </strong>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Información de Timbrado */}
      <Card className="mb-3">
        <Card.Header>
          <h5 className="mb-0">Información de Timbrado</h5>
        </Card.Header>
        <Card.Body>
          {isTimbrado ? (
            <Row>
              <Col md={6}>
                <p><strong>UUID CFDI:</strong> 
                  <Badge bg="success" className="ms-2">{invoice.cfdi_uuid}</Badge>
                  {invoice.ambiente_timbrado && (
                    <Badge bg={invoice.ambiente_timbrado === 'pruebas' ? 'warning' : 'success'} className="ms-2">
                      PAC {invoice.ambiente_timbrado === 'pruebas' ? 'Pruebas' : 'Producción'}
                    </Badge>
                  )}
                </p>
                {xmlData.timbre_fecha_timbrado && (
                  <p><strong>Fecha de Timbrado:</strong> {formatDateTimeEsMX(xmlData.timbre_fecha_timbrado, undefined, '—')}</p>
                )}
                {xmlData.timbre_rfc_prov_certif && (
                  <p><strong>RFC Proveedor de Certificación:</strong> {xmlData.timbre_rfc_prov_certif}</p>
                )}
                {xmlData.lugar_expedicion && (
                  <p><strong>Lugar de Expedición:</strong> {xmlData.lugar_expedicion}</p>
                )}
              </Col>
              <Col md={6}>
                {xmlData.no_certificado ? (
                  <p><strong>Número de Certificado:</strong> {xmlData.no_certificado}</p>
                ) : (
                  <p><strong>Número de Certificado:</strong> <span className="text-muted">Falta timbrado</span></p>
                )}
                {xmlData.timbre_no_certificado_sat ? (
                  <p><strong>No. Certificado SAT:</strong> {xmlData.timbre_no_certificado_sat}</p>
                ) : (
                  <p><strong>No. Certificado SAT:</strong> <span className="text-muted">Falta timbrado</span></p>
                )}
                {xmlData.certificado ? (
                  <p><strong>Certificado:</strong> <code className="small">{xmlData.certificado}</code></p>
                ) : (
                  <p><strong>Certificado:</strong> <span className="text-muted">Falta timbrado</span></p>
                )}
              </Col>
              {(xmlData.sello || xmlData.timbre_sello_cfd || xmlData.timbre_sello_sat) && (
                <Col md={12}>
                  <hr />
                  <p><strong>Sellos Digitales:</strong></p>
                  {xmlData.sello && (
                    <div className="mb-2">
                      <small><strong>Sello:</strong></small>
                      <code className="d-block small text-break bg-light p-2 rounded">{xmlData.sello}</code>
                    </div>
                  )}
                  {xmlData.timbre_sello_cfd && (
                    <div className="mb-2">
                      <small><strong>Sello CFD:</strong></small>
                      <code className="d-block small text-break bg-light p-2 rounded">{xmlData.timbre_sello_cfd}</code>
                    </div>
                  )}
                  {xmlData.timbre_sello_sat && (
                    <div className="mb-2">
                      <small><strong>Sello SAT:</strong></small>
                      <code className="d-block small text-break bg-light p-2 rounded">{xmlData.timbre_sello_sat}</code>
                    </div>
                  )}
                </Col>
              )}
              {/* PDF de validación SAT */}
              <Col md={12}>
                <hr />
                <p className="mb-2"><strong>PDF de validación SAT:</strong></p>
                {invoice.pdf_validacion_sat ? (
                  <div className="d-flex align-items-center gap-2">
                    <a
                      href={invoice.pdf_validacion_sat.startsWith('http') ? invoice.pdf_validacion_sat : `${window.location.origin}${invoice.pdf_validacion_sat}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-outline-primary"
                    >
                      <i className="bi bi-file-pdf me-1"></i> Ver / Descargar PDF
                    </a>
                    <span className="text-muted small">Reemplazar:</span>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf"
                      onChange={handleSubirPdfValidacion}
                      disabled={subirPdfValidacionMutation.isLoading}
                      className="form-control form-control-sm"
                      style={{ maxWidth: '220px' }}
                    />
                  </div>
                ) : (
                  <div className="d-flex align-items-center gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf"
                      onChange={handleSubirPdfValidacion}
                      disabled={subirPdfValidacionMutation.isLoading}
                      className="form-control form-control-sm"
                      style={{ maxWidth: '260px' }}
                    />
                    {subirPdfValidacionMutation.isLoading && <Spinner size="sm" />}
                    <small className="text-muted">PDF descargado del portal de verificación del SAT</small>
                  </div>
                )}
              </Col>
            </Row>
          ) : (
            <Alert variant="warning" className="mb-0">
              <i className="bi bi-exclamation-triangle me-2"></i>
              <strong>Falta timbrado:</strong> Esta factura aún no ha sido timbrada. 
              Utilice el botón &quot;Timbrar&quot; para generar el CFDI.
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Aplicaciones de pago */}
      {aplicaciones && aplicaciones.length > 0 && (
        <Card>
          <Card.Header>
            <h5 className="mb-0">Pagos Aplicados</h5>
          </Card.Header>
          <Card.Body>
            <Table responsive>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Monto</th>
                  <th>Estado</th>
                  <th>Referencia</th>
                </tr>
              </thead>
              <tbody>
                {aplicaciones.map((aplicacion) => (
                  <tr key={aplicacion.id}>
                    <td>{formatDateTimeEsMX(aplicacion.fecha_aplicacion, undefined, '—')}</td>
                    <td>
                      {formatCurrency(aplicacion.monto || 0)}
                    </td>
                    <td>
                      <Badge bg={aplicacion.estado === 'aplicado' ? 'success' : 'secondary'}>
                        {aplicacion.estado}
                      </Badge>
                    </td>
                    <td>{aplicacion.payment_reference || aplicacion.pago_reference || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* Modal de Cancelación */}
      <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isComplementoPago ? 'Cancelar complemento de pago (REP)' : 'Cancelar Factura'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            {isComplementoPago
              ? '¿Cancelar este CFDI de pagos ante el SAT? No se borrarán registros: el REP quedará cancelado y se revertirán los saldos aplicados a las facturas relacionadas (incluyendo reabrir la CxC si estaba pagada).'
              : '¿Está seguro de que desea cancelar esta factura ante el SAT?'}
          </Alert>
          <p><strong>UUID:</strong> {invoice.cfdi_uuid}</p>
          <p><strong>Folio:</strong> {folio}</p>
          <p><strong>Total:</strong> {formatCurrency(invoice.total || 0)}</p>
          {cancelRepsInfo.length > 0 && !isComplementoPago && (
            <Alert variant="danger" className="py-2">
              <strong>REP relacionados activos:</strong>
              <ul className="mb-0 small">
                {cancelRepsInfo.map((r) => (
                  <li key={r.aplicacion_id}>
                    {r.complemento_folio}
                    {r.complemento_uuid ? ` (${String(r.complemento_uuid).slice(0, 8)}…)` : ''}
                    {' — '}{formatCurrency(r.monto)}
                  </li>
                ))}
              </ul>
              Debe cancelar o revisar esos REP antes de cancelar esta factura.
            </Alert>
          )}
          {cancelErrors.reps && <Alert variant="danger" className="py-1">{cancelErrors.reps}</Alert>}
          <Form.Group className="mb-3">
            <Form.Label>Motivo de cancelación (SAT)</Form.Label>
            <Form.Select
              value={cancelMotivo}
              onChange={(e) => {
                const next = e.target.value
                setCancelMotivo(next)
                if (next !== '01') setCancelFolioSustitucion('')
                setCancelErrors((prev) => ({ ...prev, motivo: undefined, folio_sustitucion: undefined, nota: undefined }))
              }}
              isInvalid={!!cancelErrors.motivo}
            >
              {CANCEL_MOTIVOS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">{cancelErrors.motivo}</Form.Control.Feedback>
            <Form.Text className="text-muted">
              El motivo 02 es el más habitual (errores sin relación). Se envía al PAC/SAT.
            </Form.Text>
          </Form.Group>
          {cancelMotivo === '01' && (
            <Form.Group className="mb-2">
              <Form.Label>Folio fiscal sustitución (UUID en Arithmos)</Form.Label>
              <Form.Control
                type="text"
                placeholder="XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX"
                value={cancelFolioSustitucion}
                onChange={(e) => {
                  setCancelFolioSustitucion(e.target.value)
                  setCancelErrors((prev) => ({ ...prev, folio_sustitucion: undefined }))
                }}
                isInvalid={!!cancelErrors.folio_sustitucion}
              />
              <Form.Control.Feedback type="invalid">{cancelErrors.folio_sustitucion}</Form.Control.Feedback>
              <Form.Text className="text-muted">
                Debe ser el UUID de una factura vigente del mismo emisor en Arithmos.
              </Form.Text>
            </Form.Group>
          )}
          {cancelMotivo === '03' && (
            <Form.Group className="mb-2">
              <Form.Label>Documentación (operación no realizada) *</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={cancelNota}
                onChange={(e) => {
                  setCancelNota(e.target.value)
                  setCancelErrors((prev) => ({ ...prev, nota: undefined }))
                }}
                isInvalid={!!cancelErrors.nota}
                placeholder="Indique por qué no se llevó a cabo la operación…"
              />
              <Form.Control.Feedback type="invalid">{cancelErrors.nota}</Form.Control.Feedback>
            </Form.Group>
          )}
          {isCancelAdmin && (
            <Form.Check
              className="mb-2"
              type="checkbox"
              id="cancel-auth-admin"
              label="Autorización de administrador (periodo contable cerrado)"
              checked={cancelAuthAdmin}
              onChange={(e) => setCancelAuthAdmin(e.target.checked)}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
            Cerrar
          </Button>
          <Button 
            variant="danger" 
            onClick={handleCancelar}
            disabled={cancelarMutation.isLoading || cancelRepsInfo.length > 0}
          >
            {cancelarMutation.isLoading ? (
              <>
                <Spinner size="sm" className="me-1" />
                Cancelando...
              </>
            ) : (
              'Confirmar Cancelación'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de búsqueda de cuentas contables */}
      <AccountSearchModal
        show={mostrarModalCuenta}
        onHide={cerrarModalCuenta}
        onSelect={seleccionarCuenta}
        clienteId={clientId || ''}
        initialSearch={conceptoSeleccionadoCuenta?.cuenta_contable_code || ''}
        pageSize={200}
      />

      {/* Modal de Estatus */}
      <Modal show={showEstatusModal} onHide={() => setShowEstatusModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Estatus del CFDI</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {estatusData && (
            <div>
              <p><strong>UUID:</strong> {invoice.cfdi_uuid}</p>
              <p><strong>Estatus:</strong> 
                <Badge bg={estatusData.estatus === 'Vigente' ? 'success' : estatusData.estatus === 'Cancelado' ? 'danger' : 'warning'} className="ms-2">
                  {estatusData.estatus}
                </Badge>
              </p>
              {estatusData.codigo_estatus && (
                <p><strong>Código de Estatus:</strong> {estatusData.codigo_estatus}</p>
              )}
              <p><strong>Cancelable:</strong> {estatusData.es_cancelable ? 'Sí' : 'No'}</p>
              {estatusData.estatus_cancelacion && (
                <p><strong>Estatus de Cancelación:</strong> {estatusData.estatus_cancelacion}</p>
              )}
              {estatusData.error && (
                <Alert variant="danger" className="mt-3">
                  <strong>Error:</strong> {estatusData.error}
                </Alert>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEstatusModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal secundario - Vista PDF */}
      <Modal
        show={showPdfModal}
        onHide={handleCerrarPdfModal}
        size="xl"
        centered
        fullscreen="lg-down"
        dialogClassName="modal-pdf-viewer"
      >
        <Modal.Header closeButton>
          <Modal.Title>Vista previa PDF - {folio}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0 d-flex flex-column">
          {pdfBlobUrl && (
            <iframe
              src={pdfBlobUrl}
              title="PDF Factura"
              className="flex-grow-1 w-100"
              style={{
                border: 'none',
                minHeight: '80vh',
                height: '80vh'
              }}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCerrarPdfModal}>
            Cerrar
          </Button>
          <Button
            variant="primary"
            onClick={handleDescargarDesdeModal}
            disabled={descargando}
          >
            {descargando ? (
              <><Spinner size="sm" className="me-1" /> Descargando...</>
            ) : (
              <><i className="bi bi-download me-1"></i> Descargar{invoice.cfdi_uuid ? ' PDF y XML' : ''}</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <RevisarAsientoContableModal
        show={showAsientoModal}
        onHide={() => setShowAsientoModal(false)}
        title={`Revisar asiento — ${folio}`}
        propuesta={asientoPropuesta}
        isLoading={loadingAsientoPreview}
        error={asientoPreviewError}
        clientId={typeof invoice?.client === 'object' ? invoice.client?.id : (invoice?.client || invoice?.client_id)}
        isConfirming={procesarContableMutation.isLoading}
        confirmLabel="Aplicar asiento"
        onConfirm={({ lineas_contables }) => procesarContableMutation.mutate({ lineas_contables })}
      />
    </div>
  )
}

export default InvoiceDetail
